import { useNavigate, useParams } from 'react-router-dom'
import { NumberParam, useQueryParam, withDefault } from 'use-query-params'

import { ConversationRowContent, NewConversation } from '../../types'

import useSession from '../hooks/useSession'
import useConfig from '@/gf/hooks/useConfig'
import {
  FetchConversationsDocument,
  FetchMessagesForConversationDocument,
  useCreateConversationMutation,
  useCreateMessageMutation,
  useFetchConversationsQuery,
  useFetchMessagesForConversationQuery,
  useReadReceiptsForConversationQuery,
} from '../_gen/gql'
import useGqlClient from '../hooks/useGqlClient'

import ConversationM from '../modules/Conversation'
import RequestForQuote from '@/gf/modules/RequestForQuote'
import StoreOrder from '../modules/StoreOrder'
import UserM from '@/gf/modules/User'

import InboxCommon, { Conversation } from '@/gf/components/Inbox'
import Frame from '../components/Frame'
import InboxDetails from '../components/InboxDetails'
import Layout from '@/gf/components/Layout'
import Spinner from '@/gf/components/Spinner'
import Page from '@/gf/components/Page'

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'Inbox', href: '/inbox' }],
}

const conversationRowContent = (
  conversation: Conversation | NewConversation
): ConversationRowContent => ({
  name: ConversationM.inboxTitle(conversation),
  details: { type: conversation.admin ? 'admin' : 'buyer', name: conversation.name },
})

const useQueryParamOpts = withDefault(NumberParam, 1)

const Inbox = () => {
  const { conversationId } = useParams<{ conversationId: string }>() as { conversationId: string }
  const navigate = useNavigate()
  const gqlClient = useGqlClient()
  const { adminUrl } = useConfig()
  const { user, store } = useSession()
  const [page, setPage] = useQueryParam('page', useQueryParamOpts)
  const { data, error } = useFetchConversationsQuery({
    client: gqlClient,
    variables: {
      filters: [],
      page,
      pageSize: ConversationM.PAGE_SIZE,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.CONVERSATION_POLL_INTERVAL,
  })
  const [createMessage] = useCreateMessageMutation({
    client: gqlClient,
    refetchQueries: [FetchMessagesForConversationDocument, FetchConversationsDocument],
  })
  const [createConversation] = useCreateConversationMutation({
    client: gqlClient,
    refetchQueries: [FetchConversationsDocument],
  })

  const selectedConversation =
    data?.fetchConversations.conversations &&
    data.fetchConversations.conversations.find((c) => c.id === conversationId)

  const refetchConversations = () =>
    gqlClient.refetchQueries({ include: [FetchConversationsDocument] })

  const { error: messagesError, ...messagesQueryResult } = useFetchMessagesForConversationQuery({
    client: gqlClient,
    variables: {
      conversationId: selectedConversation?.id as string,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
    // Only refetch if the conversation is unread, so we can fetch the correct "read" status
    onCompleted: selectedConversation?.unreadMessages ? refetchConversations : undefined,
    skip: !conversationId || !selectedConversation || 'newConversation' in selectedConversation,
  })

  const { fetchMessagesForConversation: messages } = messagesQueryResult.data || {}

  const { readReceiptsForConversation } =
    useReadReceiptsForConversationQuery({
      client: gqlClient,
      variables: {
        conversationId: selectedConversation?.id as string,
        vendorId: null,
        vendorUserId: null,
      },
      pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
      skip: !conversationId || !selectedConversation || 'newConversation' in selectedConversation,
    }).data || {}

  const displayCustomerContactInfo = (() => {
    if (!selectedConversation) return false

    if (selectedConversation.requestForQuote)
      return RequestForQuote.canDisplayCustomerContactInfo(
        selectedConversation.requestForQuote,
        user
      )

    return selectedConversation.storeOrder
      ? StoreOrder.canDisplayCustomerContactInfo(selectedConversation.storeOrder, user)
      : false
  })()

  return (
    <Frame breadcrumbs={breadcrumbs}>
      {UserM.isAdmin(user) ? (
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p className="text-sm text-gray-700">
                Go to the{' '}
                <a
                  href={`${adminUrl}/inbox${conversationId ? `/messages/${conversationId}` : ''}`}
                  className="text-blue-500 underline"
                >
                  admin inbox page
                </a>
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      ) : (
        <Page title="Inbox">
          {error ? (
            <Layout.FullPageLayout>
              <p className="text-sm text-gray-700">{error.message}</p>
            </Layout.FullPageLayout>
          ) : !data ? (
            <Layout.FullPageLayout>
              <Spinner />
            </Layout.FullPageLayout>
          ) : data.fetchConversations.conversations.length === 0 ? (
            <Layout.FullPageLayout>
              <div className="m-8 flex justify-center text-center text-sm text-gray-600">
                No messages. You can create new messages from the RFQ details page under the
                &apos;Messages&apos; tab.
              </div>
            </Layout.FullPageLayout>
          ) : (
            <InboxCommon
              className="mt-2"
              user={user}
              conversations={data.fetchConversations.conversations}
              selectedConversation={selectedConversation}
              selectedConversationId={conversationId}
              messages={messages}
              messagesError={messagesError}
              readReceiptsForConversation={readReceiptsForConversation}
              onSelectedConversationIdChange={(selectedConversationId) =>
                selectedConversationId
                  ? navigate(`/inbox/messages/${selectedConversationId}`)
                  : navigate('/inbox')
              }
              page={page}
              setPage={setPage}
              pagination={data.fetchConversations.pagination}
              newConversations={[]}
              DetailsForConversation={InboxDetails.Conversation(store.id)}
              conversationRowContent={conversationRowContent}
              displayCustomerContactInfo={displayCustomerContactInfo}
              createMessage={(variables) =>
                createMessage({ variables: { ...variables, vendorId: null, vendorUserId: null } })
              }
              createConversation={(variables) =>
                createConversation({
                  variables: { ...variables, vendorId: null, vendorUserId: null },
                }).then((resp) => resp.data?.createConversation)
              }
            />
          )}
        </Page>
      )}
    </Frame>
  )
}

export default Inbox
